<script setup lang="ts">
  import NewUserModal from '@/components/NewUserModal.vue'
  import TopBar from '@/components/TopBar.vue'
  import { useUserStore } from '@/store/user.store'
  import type { NestUser } from '@/types/user'
  import { createFlatBaseColumns } from '@/utils/table'
  import {
    ConfirmationDialog,
    require,
    Table,
    UiButton,
    useToastStore,
  } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'

  const userStore = useUserStore()
  const router = useRouter()
  const toastStore = useToastStore()

  const users = ref<NestUser[]>([])
  const showNewUserModal = ref<boolean>(false)

  const errorMessage = ref<string | null>()
  const deletingUser = ref<NestUser | null>(null)

  const showConfirmationDialog = computed({
    get: () => deletingUser.value != null,
    set: value => {
      if (!value) deletingUser.value = null
    },
  })

  onMounted(async () => {
    await userStore.fetchMe()
    await fetchUsers()
  })

  async function fetchUsers() {
    try {
      users.value = await userStore.fetchAll(
        require(userStore.currentNestUser, 'Current nest user is missing from user store. Ensure fetchMe is called')
          .customerId
      )
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        errorMessage.value = error.response.data.message
      } else {
        throw error
      }
    }
  }

  async function deleteUser() {
    try {
      await userStore.deleteUser(
        require(deletingUser.value, 'User cannot be null when deleting a user').id
      )
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        toastStore.error('Je hebt niet de juiste rol om deze gebruiker te verwijderen')
      }
    }
    await fetchUsers()
    deletingUser.value = null
    toastStore.success('Gebruiker succesvol verwijderd')
  }
</script>

<template>
  <TopBar />
  <ConfirmationDialog
    v-model:show="showConfirmationDialog"
    :confirm="deleteUser"
    :title="`${deletingUser?.firstName} ${deletingUser?.lastName} verwijderen?`"
    body="Deze gebruiker verwijderen kan niet ongedaan gemaakt worden."
  />
  <NewUserModal v-model:show="showNewUserModal" @created="fetchUsers" />
  <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
  <div class="layout" v-else>
    <UiButton
      class="create-button"
      label="Nieuwe gebruiker"
      variant="prominent"
      icon="plus"
      @click="showNewUserModal = true"
    />
    <Table
      :rows="users"
      :columns="[
        ...createFlatBaseColumns(users, [
          'customerId',
          'permissions',
          'customer',
          'createdAt',
          'updatedAt',
          'deletedAt',
        ]),
        { key: 'actions', label: 'actions' },
      ]"
      :defaultColumns="['email', 'firstName', 'lastName', 'role', 'actions']"
    >
      <template #cell-actions="{ row }">
        <div class="button-group">
          <UiButton
            label="Details"
            variant="prominent"
            icon="chevronRight"
            @click="router.push(`/users/${row.id}`)"
          />

          <UiButton
            v-if="row.id !== userStore.currentNestUser?.id"
            variant="negative"
            icon="delete"
            @click="deletingUser = row"
          />
        </div>
      </template>
    </Table>
  </div>
</template>

<style lang="css" scoped>
  .error-message {
    color: var(--colours-support-negative);
  }

  .create-button {
    align-self: end;
  }

  .button-group {
    display: flex;
    flex-direction: row;
    gap: var(--spacing-xs);
  }

  .layout {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    gap: var(--spacing-s);
    height: 100vh;
  }
</style>
